import useSWR from 'swr';
import { api, getUserToken } from '@utils/api';

/**
 * Hook to fetch watch history
 * @param {number} limit - Number of history items to fetch
 * @returns {object} { history, isLoading, error, mutate }
 */
export const useWatchHistory = (limit = 10) => {
    const userToken = getUserToken();

    const { data, error, isLoading, mutate } = useSWR(
        userToken ? [api(`/api/user/watch-history?limit=${limit}`), userToken] : null,
        async ([url, token]) => {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch watch history');
            }

            const result = await response.json();
            return result;
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            dedupingInterval: 30000, // 30 seconds
            keepPreviousData: true
        }
    );

    return {
        history: data?.history || [],
        isLoading,
        error,
        mutate,
    };
};

/**
 * Save watch history
 * @param {string} bookId - Drama book ID
 * @param {string} chapterId - Chapter ID
 * @param {number} positionSeconds - Current position in seconds
 * @param {number} durationSeconds - Total duration in seconds
 * @param {string} bookName - Drama name
 * @param {string} coverUrl - Cover image URL
 */
export const saveWatchHistory = async (bookId, chapterId, positionSeconds, durationSeconds, bookName, coverUrl) => {
    const userToken = getUserToken();
    if (!userToken) {
        return { success: false, message: 'Not logged in' };
    }

    const payload = {
        bookId: String(bookId),
        chapterId: String(chapterId),
        positionSeconds: Math.floor(positionSeconds || 0),
        durationSeconds: Math.floor(durationSeconds || 0),
        bookName: bookName || null,
        coverUrl: coverUrl || null
    };

    try {
        const response = await fetch(api('/api/user/watch-history'), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();
        
        if (response.ok && data.success) {
            return { success: true };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        return { success: false, message: error.message };
    }
};

export default useWatchHistory;
